import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_NODE,
  GET_MKONNEKT_DISCOUNTS,
} from '../../../Constants/Config';

const initialState = {
  loading: false,
  itemDiscounts: [],
  orderDiscounts: [],
  promotionMessage: '',
  // false when the backend could not reach mKonnekt and returned an empty set.
  upstreamOk: true,
  error: '',
  requestKey: '',
  activeRequestId: null,
  activeRequestKey: '',
};

export const createMkonnektDiscountRequestKey = (requestBody) => {
  if (
    !requestBody ||
    typeof requestBody !== 'object' ||
    Array.isArray(requestBody)
  ) {
    return '';
  }

  try {
    const serializedRequest = JSON.stringify(requestBody);
    return typeof serializedRequest === 'string' ? serializedRequest : '';
  } catch {
    return '';
  }
};

// Also the longest checkout waits for a quote before proceeding without one.
export const GET_DISCOUNTS_TIMEOUT_MS = 8000;

/**
 * A UPC as mKonnekt matching uses it: trimmed text, '' when absent. Every place
 * that sends or compares a UPC goes through this so stray whitespace from the
 * catalog never breaks a match.
 */
export const normalizeMkonnektUpc = (upc) => String(upc ?? '').trim();

/**
 * The merchant id the mKonnekt quote is requested and checked for: trimmed,
 * case preserved (it is sent to the server as-is). MkonnektDiscountSync and
 * CouponTipSectionLogic must both derive it through this.
 */
export const normalizeMkonnektMerchantId = (merchantId) => {
  const normalized = String(merchantId ?? '').trim();
  const lowered = normalized.toLowerCase();
  return lowered === 'null' || lowered === 'undefined' ? '' : normalized;
};

const PAYMENT_CART_ROUTE_PATTERN = /^\/payment-cart\/([^/]+)\/?$/i;

/**
 * The store whose cart is being quoted. On checkout (/payment-cart/:id) that is
 * the route merchant — the exact id CouponTipSectionLogic receives from
 * MainCart — so the quote is always requested for the store being checked out.
 * Elsewhere (cart drawer) it falls back to the last-visited merchant.
 */
export const resolveMkonnektMerchantId = (pathname, storedMerchantId) => {
  const match =
    typeof pathname === 'string' ? pathname.match(PAYMENT_CART_ROUTE_PATTERN) : null;
  if (match?.[1]) {
    let routeMerchantId = match[1];
    try {
      routeMerchantId = decodeURIComponent(routeMerchantId);
    } catch {
      // keep the raw segment
    }
    return normalizeMkonnektMerchantId(routeMerchantId);
  }
  return normalizeMkonnektMerchantId(storedMerchantId);
};

/**
 * The quote to price with: the slice's discounts only when they were settled
 * for `requestBody` (the current cart's request). Anything else — no request,
 * in flight, timed out, or a quote for a different cart — prices the cart with
 * no mKonnekt discounts.
 */
export const selectMatchingMkonnektDiscounts = (mkonnektDiscounts, requestBody) => {
  const requestKey = createMkonnektDiscountRequestKey(requestBody);
  if (requestKey && mkonnektDiscounts?.requestKey === requestKey) {
    return mkonnektDiscounts;
  }
  return { itemDiscounts: [], orderDiscounts: [], promotionMessage: '' };
};

/**
 * mKonnekt's `mobileNumber` (loyalty id): exactly 10 US digits, or '' when the
 * value can't be one. Strips formatting and a leading country code `1`.
 */
export const normalizeMkonnektMobileNumber = (mobileNumber) => {
  const digits = String(mobileNumber ?? '').replace(/\D/g, '');
  const national =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return national.length === 10 ? national : '';
};

// MkonnektDiscountSync (which dispatches the request) and CouponTipSectionLogic
// (which rebuilds it to check readiness) must both build the body through this
// function with the same inputs — the request key is the serialized body.
export const createMkonnektDiscountRequestBody = ({
  cartItems,
  rcn,
  mobileNumber,
  merchantId,
}) => {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return null;

  // Only UPC-bearing lines can carry a manufacturer discount; with none there
  // is nothing to quote (no request, not a failure).
  const upcLines = cartItems.filter((line) => normalizeMkonnektUpc(line?.upcCode));
  if (upcLines.length === 0) return null;

  const items = upcLines.map((line) => {
    const parsedQuantity = Number(line?.selectedQuantity);
    const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0
      ? parsedQuantity
      : 1;
    const linePrice = Number.parseFloat(line?.price);
    const normalizedLinePrice = Number.isFinite(linePrice) ? linePrice : 0;
    const price = Number.parseFloat(
      (normalizedLinePrice / quantity).toFixed(2),
    );

    return {
      upcCode: normalizeMkonnektUpc(line?.upcCode),
      categoryName: line?.categoryName || '',
      name: line?.product?.title ?? '',
      price,
      quantity,
      unit: line?.uom || line?.consumer_unit || '',
    };
  });
  const totalAmount = Number.parseFloat(
    items
      .reduce((sum, item) => sum + item.price * item.quantity, 0)
      .toFixed(2),
  );

  return {
    couponCodes: [],
    id: '',
    items,
    merchant: { loyaltyAtLineItemLevel: true, rcn: String(rcn ?? '') },
    merchant_id: normalizeMkonnektMerchantId(merchantId),
    mobileNumber: normalizeMkonnektMobileNumber(mobileNumber),
    totalAmount,
  };
};

/**
 * The three saveOrder fields the server-side mKonnekt reporting sweep reads:
 *   mkonnekt_discount_response  JSON of the untouched quote used for this sale
 *   mkonnekt_mobile_number      the mobileNumber that quote was requested with
 *   mkonnekt_rcn                the rcn that quote was requested with
 *
 * `requestBody` is the body checkout rebuilds for the CURRENT cart (the same
 * one isMkonnektPricingReady keys on). The quote is only sent when the slice
 * settled on exactly that request; otherwise (dezi off, no rcn, stale quote
 * for a different cart) every field is '' so the sweep never reports a quote
 * taken on another cart. mobileNumber / rcn are read from the body itself so
 * they are exactly what the quote was requested with.
 */
export const buildMkonnektOrderQuoteFields = ({
  isDeziEnabled,
  mkonnektDiscounts,
  requestBody,
}) => {
  const empty = {
    mkonnekt_discount_response: '',
    mkonnekt_mobile_number: '',
    mkonnekt_rcn: '',
  };
  if (!isDeziEnabled) return empty;

  const requestKey = createMkonnektDiscountRequestKey(requestBody);
  if (!requestKey || mkonnektDiscounts?.requestKey !== requestKey) {
    return empty;
  }

  return {
    mkonnekt_discount_response: JSON.stringify({
      itemDiscounts: mkonnektDiscounts?.itemDiscounts ?? [],
      orderDiscounts: mkonnektDiscounts?.orderDiscounts ?? [],
      promotionMessage: String(mkonnektDiscounts?.promotionMessage ?? ''),
      upstream_ok: mkonnektDiscounts?.upstreamOk !== false,
    }),
    mkonnekt_mobile_number: String(requestBody?.mobileNumber ?? ''),
    mkonnekt_rcn: String(requestBody?.merchant?.rcn ?? ''),
  };
};

// Fetches applicable mKonnekt tobacco discounts for the current cart.
// `requestBody` is the mKonnekt request shape assembled by MkonnektDiscountSync.
export const fetchMkonnektDiscounts = createAsyncThunk(
  'mkonnektDiscounts/fetchMkonnektDiscounts',
  async (requestBody) => {
    const response = await axios.post(
      BASE_URL_NODE + GET_MKONNEKT_DISCOUNTS,
      requestBody,
      {
        headers: { 'Content-Type': 'application/json' },
        // A slow quote must not hold checkout hostage; a timeout rejects and
        // the cart proceeds without tobacco discounts.
        timeout: GET_DISCOUNTS_TIMEOUT_MS,
      },
    );
    // Backend returns stringified numerics ("200"), so gate loosely.
    if (Number(response.data.status) === 200) {
      // { orderDiscounts, itemDiscounts, promotionMessage, upstream_ok }
      return response.data.result;
    }
    throw new Error('Server error');
  },
);

const mkonnektDiscountSlice = createSlice({
  name: 'mkonnektDiscounts',
  initialState,
  reducers: {
    // Cleared for non-dezi merchants / empty carts so stale discounts never apply.
    clearMkonnektDiscounts: (state) => {
      state.itemDiscounts = [];
      state.orderDiscounts = [];
      state.promotionMessage = '';
      state.upstreamOk = true;
      state.error = '';
      state.loading = false;
      state.requestKey = '';
      state.activeRequestId = null;
      state.activeRequestKey = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMkonnektDiscounts.pending, (state, action) => {
      const requestId = action.meta?.requestId;
      if (!requestId) return;

      const requestKey = createMkonnektDiscountRequestKey(action.meta?.arg);
      if (state.requestKey !== requestKey) {
        state.itemDiscounts = [];
        state.orderDiscounts = [];
        state.promotionMessage = '';
        state.upstreamOk = true;
        state.requestKey = '';
      }
      state.loading = true;
      state.error = '';
      state.activeRequestId = requestId;
      state.activeRequestKey = requestKey;
    });
    builder.addCase(fetchMkonnektDiscounts.fulfilled, (state, action) => {
      const requestKey = createMkonnektDiscountRequestKey(action.meta?.arg);
      if (
        action.meta?.requestId !== state.activeRequestId ||
        requestKey !== state.activeRequestKey
      ) {
        return;
      }

      state.loading = false;
      state.itemDiscounts = action.payload?.itemDiscounts ?? [];
      state.orderDiscounts = action.payload?.orderDiscounts ?? [];
      state.promotionMessage = String(action.payload?.promotionMessage ?? '');
      state.upstreamOk = action.payload?.upstream_ok !== false;
      state.error = '';
      state.requestKey = requestKey;
      state.activeRequestId = null;
      state.activeRequestKey = '';
    });
    builder.addCase(fetchMkonnektDiscounts.rejected, (state, action) => {
      const requestKey = createMkonnektDiscountRequestKey(action.meta?.arg);
      if (
        action.meta?.requestId !== state.activeRequestId ||
        requestKey !== state.activeRequestKey
      ) {
        return;
      }

      // A failed quote settles THIS cart as "no tobacco discounts": recording
      // its key lets checkout proceed instead of blocking on a lookup that will
      // not be retried for an unchanged cart. `error` is kept for diagnostics
      // only. A later cart change produces a new key, and the pending handler
      // clears this one before re-requesting.
      state.loading = false;
      state.itemDiscounts = [];
      state.orderDiscounts = [];
      state.promotionMessage = '';
      state.upstreamOk = false;
      state.error = action.error?.message || 'Unable to load mKonnekt discounts';
      state.requestKey = requestKey;
      state.activeRequestId = null;
      state.activeRequestKey = '';
    });
  },
});

export const { clearMkonnektDiscounts } = mkonnektDiscountSlice.actions;
export default mkonnektDiscountSlice.reducer;
